import axios from 'axios';
import { getTokens, isUsableRefreshToken, refreshAccessToken } from './tokens';

const STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
};

export function createApiInstance(baseURL) {
  const opts = {
    baseURL,
    headers: {},
    withCredentials: true,
  };

  const tokens = getTokens();

  if (tokens?.token) {
    opts.headers.Authorization = `Bearer ${tokens.token}`;
  }

  const instance = axios.create(opts);

  // When a request is sent along with { headers: { retry: true } },
  // get that value from the configured headers, set it in a custom field,
  // and delete the entry from the headers. This is the only way to get
  // a flag passed along with Axios via interceptors since custom fields
  // in AxiosRequestConfig are automatically ignored by the axios library.
  instance.interceptors.request.use(config => {
    const { retry } = config.headers;
    if (retry) {
      // Since the headers was repurposed to pass along the retry param,
      // delete the entry from the headers and set it within a custom field
      // of the config. This field will eventually make its way to the response
      // interceptor for the instance.
      config.custom = { retry };
      delete config.headers.retry;
    }
    return config;
  });

  // When a request fails and a refresh token is available, attempt to refresh
  // and obtain a new access token. If that's successful, save the new access
  // token and use it to retry the original request.
  instance.interceptors.response.use(null, async error => {
    const retryCodes = [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED];

    const shouldRetry = Boolean(
      error.config &&
        error.response &&
        error.config.custom &&
        error.config.custom.retry &&
        retryCodes.includes(error.response.status) &&
        isUsableRefreshToken(getTokens()?.refresh_token),
    );

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    // Posted with the bare axios rather than `instance`, so a 401 on the
    // refresh itself cannot re-enter this interceptor.
    const refreshed = await refreshAccessToken({
      refreshToken: getTokens()?.refresh_token,
      transport: (path, body) => axios.post(`${baseURL}/${path}`, body),
    });

    if (refreshed.status !== 'refreshed') {
      // A 401 from the refresh endpoint means the refresh token is no longer
      // valid (for example, it expired). Mark the original request error so
      // callers can distinguish this from other auth/bootstrap failures.
      if (refreshed.failure?.response?.status === STATUS_CODES.UNAUTHORIZED) {
        error.isRefreshTokenExpired = true;
      }

      // Throw the error from the original request, which is the failure the
      // caller asked about.
      throw error;
    }

    const { config } = error;

    // Set the new token into the instance headers
    instance.defaults.headers.Authorization = `Bearer ${refreshed.token}`;

    // Set the new token into the request that will be retried
    config.headers.Authorization = `Bearer ${refreshed.token}`;

    // Retry the original request
    return axios.request(config);
  });

  return instance;
}
